import React, { createContext, useContext, useMemo } from 'react';
import { useParams, Outlet, useLocation } from 'react-router-dom';
import {
  usePlmCurtailmentTasksByPropertyQuery,
  usePlmCurtailmentTasksByPropertyIdSubscriptionSubscription,
} from '../../../types/generated-types';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import { hasActiveOrPendingPlmTasks } from './plmTaskVisibility';
import { shouldLoadPropertyPlmTasks } from './propertyPlmRouteLoading';

interface PlmCurtailmentTask {
  _id: string;
  plmCurtailmentActionId: string;
  plmEventResponseId: string;
  batchId: number;
  propertyId: string;
  unitId: string;
  deviceId: string;
  deviceType: string;
  scheduledStart: number;
  scheduledEnd: number;
  startedAt?: number;
  verifiedAt?: number;
  verifyAttempts: number;
  endedAt?: number;
  status: string;
  statusMessage: string;
  estimatedReductionKW?: number;
}

interface UnitPlmTasks {
  currentTasks: PlmCurtailmentTask[];
  scheduledTasks: PlmCurtailmentTask[];
}

interface PropertyPlmContextValue {
  propertyId: string;
  hasScheduledEventResponses: boolean;
  unitPlmTasks: Record<string, UnitPlmTasks>;
  loading: boolean;
  error?: any;
}

const PropertyPlmContext = createContext<PropertyPlmContextValue | undefined>(
  undefined,
);

export const usePropertyPlmContext = () => {
  const context = useContext(PropertyPlmContext);
  if (!context) {
    throw new Error(
      'usePropertyPlmContext must be used within PropertyContextWrapper',
    );
  }
  return context;
};

export const PropertyContextWrapper: React.FC = () => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const { can } = useAuthorizer();
  const canAccessPlm = can('be', 'super');
  const shouldLoadPlmTasks = shouldLoadPropertyPlmTasks({
    canAccessPlm,
    pathname: location.pathname,
  });

  // Extract property ID from params or fallback to pathname
  // Handle case where id might contain additional path segments (e.g., "propertyId/installer")
  let propertyId = params.id;
  if (propertyId && propertyId.includes('/')) {
    // If id contains path separators, extract just the first part as the property ID
    propertyId = propertyId.split('/')[0];
  }

  if (!propertyId) {
    const pathParts = location.pathname.split('/');
    const propertyIndex = pathParts.findIndex((part) => part === 'properties');
    if (propertyIndex !== -1 && pathParts[propertyIndex + 1]) {
      propertyId = pathParts[propertyIndex + 1];
    }
  }

  // PLM task state is restricted to super users.
  const {
    data: curtailmentTasksData,
    loading,
    error,
    refetch: refetchCurtailmentTasks,
  } = usePlmCurtailmentTasksByPropertyQuery({
    variables: { propertyId: propertyId || '' },
    skip: !propertyId || !shouldLoadPlmTasks,
    fetchPolicy: 'cache-and-network',
  });

  // Subscribe to PLM curtailment task changes to trigger real-time updates
  usePlmCurtailmentTasksByPropertyIdSubscriptionSubscription({
    variables: { propertyId: propertyId || '' },
    skip: !propertyId || !shouldLoadPlmTasks,
    onData: ({ data }) => {
      if (data?.data?.plmCurtailmentTasksByPropertyIdEvents) {
        const curtailmentTask = data.data.plmCurtailmentTasksByPropertyIdEvents;
        // Check if this curtailment task affects our property
        if (curtailmentTask.propertyId === propertyId) {
          // Refetch curtailment tasks when any task changes
          refetchCurtailmentTasks();
        }
      }
    },
  });

  // Determine if there are any actionable PLM events based on curtailment tasks data
  const hasScheduledEventResponses = useMemo(() => {
    if (!curtailmentTasksData?.plmCurtailmentTasksByProperty) {
      return false;
    }

    const tasks =
      curtailmentTasksData.plmCurtailmentTasksByProperty as PlmCurtailmentTask[];

    return hasActiveOrPendingPlmTasks(tasks);
  }, [curtailmentTasksData]);

  // Process curtailment tasks into unit-organized structure
  const unitPlmTasks = useMemo(() => {
    if (!curtailmentTasksData?.plmCurtailmentTasksByProperty) {
      return {};
    }

    const tasks =
      curtailmentTasksData.plmCurtailmentTasksByProperty as PlmCurtailmentTask[];
    const currentTimestamp = Date.now();

    const result: Record<string, UnitPlmTasks> = {};

    tasks.forEach((task) => {
      const unitId = task.unitId;

      if (!result[unitId]) {
        result[unitId] = {
          currentTasks: [],
          scheduledTasks: [],
        };
      }

      // Determine if task is current or scheduled
      const isCurrent =
        task.status === 'active' &&
        task.startedAt &&
        task.startedAt <= currentTimestamp &&
        (!task.endedAt || task.endedAt > currentTimestamp);

      const isScheduled =
        task.status === 'scheduled' && task.scheduledStart > currentTimestamp;

      if (isCurrent) {
        result[unitId].currentTasks.push(task);
      } else if (isScheduled) {
        result[unitId].scheduledTasks.push(task);
      }
    });

    return result;
  }, [curtailmentTasksData]);

  const contextValue: PropertyPlmContextValue = useMemo(
    () => ({
      propertyId: propertyId || '',
      hasScheduledEventResponses,
      unitPlmTasks,
      loading: shouldLoadPlmTasks ? loading : false,
      error: shouldLoadPlmTasks ? error : undefined,
    }),
    [
      propertyId,
      hasScheduledEventResponses,
      unitPlmTasks,
      shouldLoadPlmTasks,
      loading,
      error,
    ],
  );

  // Ensure we have a valid property ID
  if (!propertyId) {
    console.error('PropertyContextWrapper: No property ID found');
    return <div>Property ID is required</div>;
  }

  return (
    <PropertyPlmContext.Provider value={contextValue}>
      <Outlet />
    </PropertyPlmContext.Provider>
  );
};
